import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { AI_BASE_URL, aiApiKeyHeaders } from "../../lib/aiApiBaseUrl"

function getBearerToken(getState) {
  const state = getState?.() || {}
  const auth = state.auth || {}
  return (
    auth.token ||
    auth.accessToken ||
    auth.user?.token ||
    (typeof localStorage !== "undefined" ? localStorage.getItem("token") : null)
  )
}

/**
 * POST audit payload to AI service (website / social audit).
 */
export const postAudit = createAsyncThunk(
  "audit/postAudit",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const token = getBearerToken(getState)
      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      const url = `${String(AI_BASE_URL).replace(/\/$/, "")}/api/audit`
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...aiApiKeyHeaders(),
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        let message = `Request failed (${response.status})`
        try {
          const errJson = await response.json()
          message = errJson?.message || errJson?.error || message
        } catch {
          const text = await response.text().catch(() => "")
          if (text) message = text
        }
        throw new Error(message)
      }

      return await response.json()
    } catch (err) {
      console.error("Audit API error:", err)
      return rejectWithValue(err?.message || "Audit request failed")
    }
  },
)

const initialState = {
  status: "idle",
  error: null,
  lastResult: null,
}

const auditSlice = createSlice({
  name: "audit",
  initialState,
  reducers: {
    clearAuditError: (state) => {
      state.error = null
    },
    resetAudit: (state) => {
      state.status = "idle"
      state.error = null
      state.lastResult = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postAudit.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(postAudit.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.lastResult = action.payload
      })
      .addCase(postAudit.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to run audit"
      })
  },
})

export const { clearAuditError, resetAudit } = auditSlice.actions
export default auditSlice.reducer
